import { LocalizedField } from "src/utils/interfaces/cv-search/CvSearchInterfaces"
import { JobFilterItemCount } from "src/utils/interfaces/job/Job.store"

export type JobTitleNameSource = {
    jobTitleName?: string
    jobTitleNameBothLanguages?: Partial<LocalizedField>
    jobTitleId?: string
    position?: string
    name?: Partial<LocalizedField>
}

const GENERIC_JOB_TITLE_OTHER_LABELS = new Set(["other", "others", "أخرى", "اخرى"])

export const isGenericJobTitleOtherLabel = (value: string | undefined | null): boolean => {
    if (!value?.trim()) return false
    return GENERIC_JOB_TITLE_OTHER_LABELS.has(value.trim().toLowerCase())
}

export const getLocalizedFieldValue = (
    field: Partial<LocalizedField> | null | undefined,
    lang: "en" | "ar"
): string | undefined => {
    if (!field) return undefined

    const fallbackLang = lang === "en" ? "ar" : "en"
    const primary = field[lang]?.trim()
    if (primary && !isGenericJobTitleOtherLabel(primary)) return primary

    const secondary = field[fallbackLang]?.trim()
    if (secondary && !isGenericJobTitleOtherLabel(secondary)) return secondary

    return primary || secondary || undefined
}

export const resolveJobTitleName = (
    job: JobTitleNameSource,
    lang: "en" | "ar",
    filterTitleLookup?: JobFilterItemCount[]
): string | undefined => {
    const fromBoth = getLocalizedFieldValue(job.jobTitleNameBothLanguages, lang)
    if (fromBoth) return fromBoth

    const fromNameField = getLocalizedFieldValue(job.name, lang)
    if (fromNameField) return fromNameField

    const direct = job.jobTitleName?.trim()
    if (direct && !isGenericJobTitleOtherLabel(direct)) return direct

    if (filterTitleLookup?.length && job.jobTitleId) {
        const filterItem = filterTitleLookup.find((item) => item.id === job.jobTitleId)
        const fromFilter = getLocalizedFieldValue(filterItem?.name, lang)
        if (fromFilter) return fromFilter
    }

    return undefined
}

export const formatJobTitleWithPositionFallback = (
    job: JobTitleNameSource,
    lang: "en" | "ar",
    enumLabel: (key: string) => string,
    filterTitleLookup?: JobFilterItemCount[]
): string => {
    const resolved = resolveJobTitleName(job, lang, filterTitleLookup)
    if (resolved) return resolved

    if (job.position && job.position !== "OTHER") {
        return enumLabel(`enum_${job.position}`)
    }

    return enumLabel("enum_OTHER")
}

export type JobTitleFormValue = {
    objectID?: string
    value?: string
    displayName?: string
    label?: string
    freeJobTitle?: string
    isCustom?: boolean
    __isNew__?: boolean
}

export const stripJobTitleDisplayHtml = (value: string | undefined): string =>
    (value ?? "").replace(/<em>/g, "").replace(/<\/em>/g, "").trim()

/** Collapses runs of whitespace and trims ends (leading/trailing/multiple internal spaces). */
export const normalizeJobTitleWhitespace = (value: string): string =>
    value.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim()

/** Stripped, lowercased text for case-insensitive match (removes Typesense `<em>` markup). */
export const normalizeJobTitleForCreatableCompare = (value: string | undefined | null): string =>
    normalizeJobTitleWhitespace(stripJobTitleDisplayHtml(value ?? "")).toLowerCase()

type JobTitleCreatableAccessors = {
    getOptionLabel: (option: any) => string
    getOptionValue: (option: any) => string
}

/**
 * `isValidNewOption` for job-title AsyncCreatable: hide the custom row when the input
 * matches a lookup option after stripping highlight markup (case-insensitive), including the current value.
 */
export const isJobTitleCreatableNewOptionValid = (
    inputValue: string,
    selectValue: unknown,
    selectOptions: readonly any[] | undefined,
    accessors: JobTitleCreatableAccessors,
): boolean => {
    const candidate = normalizeJobTitleForCreatableCompare(inputValue)
    if (!candidate) return false

    const selected = Array.isArray(selectValue) ? selectValue : selectValue != null ? [selectValue] : []
    for (const v of selected) {
        if (!v || v.__isNew__) continue
        if (normalizeJobTitleForCreatableCompare(accessors.getOptionLabel(v)) === candidate) {
            return false
        }
    }

    for (const opt of selectOptions ?? []) {
        if (!opt || opt.__isNew__ || opt.disabled) continue
        if (!opt.objectID) continue
        if (normalizeJobTitleForCreatableCompare(accessors.getOptionLabel(opt)) === candidate) {
            return false
        }
    }

    return true
}

/** Resolves the free-text title from form state (creatable select or template without lookup id). */
export const getFreeJobTitleFromForm = (
    jt: JobTitleFormValue | undefined,
    fallbackName?: string,
): string | undefined => {
    const fromForm = stripJobTitleDisplayHtml(jt?.freeJobTitle || jt?.displayName || jt?.label)
    if (fromForm) return fromForm
    return stripJobTitleDisplayHtml(fallbackName) || undefined
}

/** True when the title is user-entered text rather than a lookup job title id. */
export const isCustomJobTitleFormValue = (
    jt: JobTitleFormValue | undefined,
    fallbackJobTitleId?: string,
): boolean => {
    if (!jt) return false
    if (jt.isCustom || jt.__isNew__) return true
    const lookupId = jt.objectID || jt.value || fallbackJobTitleId
    if (lookupId) return false
    return !!getFreeJobTitleFromForm(jt)
}

export type JobTitlePostFields = {
    jobTitleId?: string
    jobTitleName?: string
    freeJobTitle?: string
}

export const buildJobTitlePostFields = (
    jt: JobTitleFormValue | undefined,
    options?: { fallbackJobTitleId?: string; fallbackJobTitleName?: string },
): JobTitlePostFields => {
    const isCustom = isCustomJobTitleFormValue(jt, options?.fallbackJobTitleId)
    if (isCustom) {
        return {
            freeJobTitle: getFreeJobTitleFromForm(jt, options?.fallbackJobTitleName),
        }
    }
    return {
        jobTitleId: jt?.objectID || options?.fallbackJobTitleId || undefined,
        jobTitleName:
            getFreeJobTitleFromForm(jt) ||
            stripJobTitleDisplayHtml(options?.fallbackJobTitleName) ||
            undefined,
    }
}
